// API service layer for Supabase
import { supabase, type DatabaseUser, type DatabaseLeague, type DatabaseRound, type DatabaseSubmission, type DatabaseVote, type DatabaseActivity, type DatabaseLeagueMember, type DatabasePlaylistComment, type DatabaseSeason, type DatabasePlaylistLink } from './supabase';
import type { User, League, Round, Submission, Vote, Activity, LeagueScoring, PlaylistComment, Season } from './mock-data';
import { hashPassword, verifyPassword } from './auth';

// Helper function to check if Supabase is configured
function checkSupabaseConfig(): boolean {
  const url = import.meta.env.VITE_SUPABASE_URL;
  const key = import.meta.env.VITE_SUPABASE_ANON_KEY;
  return !!(url && key && url !== 'https://placeholder.supabase.co' && key !== 'placeholder-key');
}

// Convert database types to app types
function dbUserToUser(dbUser: DatabaseUser): User {
  return {
    id: dbUser.id,
    username: dbUser.username,
    email: dbUser.email,
    avatar_url: dbUser.avatar_url || undefined,
    favorite_genre: dbUser.favorite_genre || undefined,
    total_leagues: 0, // Will be calculated from league_members
    total_wins: 0, // Will be calculated from votes/submissions
    total_submissions: dbUser.total_submissions || 0,
    isAdmin: dbUser.is_admin || false,
  };
}

function dbLeagueToLeague(dbLeague: DatabaseLeague, memberCount: number, currentRound?: Round): League {
  return {
    id: dbLeague.id,
    name: dbLeague.name,
    description: dbLeague.description || '',
    invite_code: dbLeague.invite_code,
    created_by: dbLeague.created_by,
    max_players: dbLeague.max_players,
    points_per_round: dbLeague.points_per_round,
    scoring: dbLeague.scoring as LeagueScoring,
    require_spotify: dbLeague.require_spotify,
    is_public: dbLeague.is_public,
    member_count: memberCount,
    current_round: currentRound,
    created_at: dbLeague.created_at,
  };
}

function dbRoundToRound(dbRound: DatabaseRound & { seasons?: { name: string } | null }): Round {
  // New schema: season name lives on seasons.name via rounds.season_id.
  // Old schema: rounds.season contained the season name directly.
  const seasonName = (dbRound as any)?.seasons?.name || dbRound.season || undefined;
  
  // Debug logging to see what we're getting
  if (!seasonName) {
    console.log('[dbRoundToRound] Round missing season name:', {
      roundId: dbRound.id,
      theme: dbRound.theme,
      seasonField: dbRound.season,
      seasonId: (dbRound as any).season_id
    });
  }
  
  return {
    id: dbRound.id,
    league_id: dbRound.league_id,
    round_number: dbRound.round_number,
    theme: dbRound.theme,
    description: dbRound.description || undefined,
    season: seasonName,
    submission_deadline: dbRound.submission_deadline,
    voting_deadline: dbRound.voting_deadline,
    status: dbRound.status,
    created_at: dbRound.created_at,
  };
}

function dbSubmissionToSubmission(dbSubmission: DatabaseSubmission): Submission {
  return {
    id: dbSubmission.id,
    round_id: dbSubmission.round_id,
    user_id: dbSubmission.user_id,
    song_title: dbSubmission.song_title,
    artist_name: dbSubmission.artist_name,
    spotify_track_id: dbSubmission.spotify_track_id || undefined,
    spotify_url: dbSubmission.spotify_url || undefined,
    youtube_video_id: dbSubmission.youtube_video_id || undefined,
    video_url: dbSubmission.youtube_url || undefined,
    submitter_comment: dbSubmission.submitter_comment || undefined,
    fun_facts: dbSubmission.fun_facts || undefined,
    total_points: dbSubmission.total_points || 0,
    submitted_at: dbSubmission.submitted_at,
    comments_imported: dbSubmission.comments_imported || undefined,
    imported_at: dbSubmission.imported_at || undefined,
    import_source: dbSubmission.import_source || undefined,
  };
}

function dbVoteToVote(dbVote: DatabaseVote): Vote {
  return {
    id: dbVote.id,
    round_id: dbVote.round_id,
    voter_id: dbVote.user_id,
    submission_id: dbVote.submission_id,
    points: dbVote.points,
    comment: dbVote.comment || undefined,
    created_at: dbVote.created_at,
  };
}

function dbActivityToActivity(dbActivity: DatabaseActivity): Activity {
  return {
    id: dbActivity.id,
    type: dbActivity.type,
    message: dbActivity.message,
    time: dbActivity.created_at,
    user_id: dbActivity.user_id || undefined,
    league_id: dbActivity.league_id || undefined,
    round_id: dbActivity.round_id || undefined,
  };
}

function dbSeasonToSeason(dbSeason: DatabaseSeason): Season {
  return {
    id: dbSeason.id,
    league_id: dbSeason.league_id,
    name: dbSeason.name,
    description: dbSeason.description || undefined,
    invite_code: dbSeason.invite_code,
    created_by: dbSeason.created_by || '',
    max_players: dbSeason.max_players,
    points_per_round: dbSeason.points_per_round,
    scoring: dbSeason.scoring as LeagueScoring,
    require_spotify: dbSeason.require_spotify,
    status: dbSeason.status,
    sort_index: dbSeason.sort_index ?? undefined,
    created_at: dbSeason.created_at,
  };
}

// User API
export const userAPI = {
  async register(user: { username: string; email: string; password: string; musicleagueUsername?: string; isTransferPage?: boolean }): Promise<{ user: User; isImportedUser: boolean; alreadyLinked?: boolean }> {
    if (!checkSupabaseConfig()) {
      throw new Error('Supabase is not configured. Please set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.');
    }

    // Check if user already exists (normalize email and username for comparison)
    const normalizedEmail = user.email.trim().toLowerCase();
    const normalizedUsername = user.username.trim();
    const normalizedMusicLeagueUsername = user.musicleagueUsername?.trim();

    // If MusicLeague username is provided, try to match it first
    if (normalizedMusicLeagueUsername) {
      const { data: musicLeagueUser } = await supabase
        .from('users')
        .select('id, username, email, password_hash')
        .ilike('username', normalizedMusicLeagueUsername)
        .maybeSingle();

      if (musicLeagueUser) {
        console.log(`[Register] Found MusicLeague user:`, {
          username: musicLeagueUser.username,
          email: musicLeagueUser.email,
          password_hash_start: musicLeagueUser.password_hash?.substring(0, 30),
        });

        const isImportedPassword = musicLeagueUser.password_hash === 'IMPORTED_USER_PASSWORD_RESET_REQUIRED';
        const isPlaceholderEmail = musicLeagueUser.email?.endsWith('@imported.local') || 
                                    musicLeagueUser.email?.endsWith('@archive.local');
        
        const isImportedUser = isImportedPassword || isPlaceholderEmail;
        
        if (isImportedUser) {
          // Link the account - update with new email and password
          console.log(`[Register] ✅ Linking MusicLeague account: ${normalizedMusicLeagueUsername}`);
          const passwordHash = await hashPassword(user.password);
          
          const { data: updatedUser, error: updateError } = await supabase
            .from('users')
            .update({
              username: normalizedUsername, // Allow username change
              email: normalizedEmail,
              password_hash: passwordHash,
            })
            .eq('id', musicLeagueUser.id)
            .select()
            .single();

          if (updateError) {
            console.error('[Register] ❌ Failed to link MusicLeague account:', updateError);
            throw new Error(`Failed to link account: ${updateError.message}`);
          }

          console.log(`[Register] ✅ Successfully linked MusicLeague account: ${updatedUser.username}`);
          return { user: dbUserToUser(updatedUser), isImportedUser: true };
        } else {
          // MusicLeague username exists but is already active
          if (user.isTransferPage) {
            // On transfer page, log them in instead of showing error
            console.log(`[Register] Account already linked, attempting login for: ${normalizedMusicLeagueUsername}`);
            
            // Try to log in with the provided credentials
            try {
              const loggedInUser = await this.login({ email: normalizedEmail, password: user.password });
              return { user: loggedInUser, isImportedUser: false, alreadyLinked: true };
            } catch (loginError) {
              // If login fails, try with the existing email
              try {
                const loggedInUser = await this.login({ email: musicLeagueUser.email || '', password: user.password });
                return { user: loggedInUser, isImportedUser: false, alreadyLinked: true };
              } catch {
                // If both fail, show helpful error
                throw new Error(`The MusicLeague username "${normalizedMusicLeagueUsername}" is already linked. Please sign in with your existing credentials. If you think this is an error, please contact us via the feedback form.`);
              }
            }
          } else {
            throw new Error(`The MusicLeague username "${normalizedMusicLeagueUsername}" is already linked to an active account. If this is you, please sign in instead.`);
          }
        }
      } else {
        // MusicLeague username not found - proceed with normal registration
        console.log(`[Register] MusicLeague username "${normalizedMusicLeagueUsername}" not found, proceeding with new account`);
      }
    }

    // Check for existing email (but allow if it's the same user we're activating)
    const { data: existingEmail } = await supabase
      .from('users')
      .select('id, username, password_hash')
      .eq('email', normalizedEmail)
      .maybeSingle();

    if (existingEmail && existingEmail.password_hash !== 'IMPORTED_USER_PASSWORD_RESET_REQUIRED') {
      throw new Error('Email already registered');
    }

    // Check for existing username (case-insensitive)
    const { data: existingUsername } = await supabase
      .from('users')
      .select('id, username, email, password_hash')
      .ilike('username', normalizedUsername)
      .maybeSingle();

    // If username exists, check if it's an imported user
    if (existingUsername) {
      console.log(`[Register] Found existing user:`, {
        username: existingUsername.username,
        email: existingUsername.email,
        password_hash_start: existingUsername.password_hash?.substring(0, 30),
      });

      const isImportedPassword = existingUsername.password_hash === 'IMPORTED_USER_PASSWORD_RESET_REQUIRED';
      const isPlaceholderEmail = existingUsername.email?.endsWith('@imported.local') || 
                                  existingUsername.email?.endsWith('@archive.local');
      
      console.log(`[Register] Import detection:`, {
        isImportedPassword,
        isPlaceholderEmail,
        email: existingUsername.email,
      });
      
      // Consider it imported if it has placeholder password OR placeholder email
      const isImportedUser = isImportedPassword || isPlaceholderEmail;
      
      if (isImportedUser) {
        // This is an imported user - update their email and password
        console.log(`[Register] ✅ Activating imported user: ${normalizedUsername} (detected by ${isImportedPassword ? 'password' : 'email'})`);
        const passwordHash = await hashPassword(user.password);
        
        const { data: updatedUser, error: updateError } = await supabase
          .from('users')
          .update({
            email: normalizedEmail,
            password_hash: passwordHash,
          })
          .eq('id', existingUsername.id)
          .select()
          .single();

        if (updateError) {
          console.error('[Register] ❌ Failed to activate imported user:', updateError);
          throw new Error(`Failed to activate account: ${updateError.message}`);
        }

        console.log(`[Register] ✅ Successfully activated user: ${updatedUser.username}`);
        return { user: dbUserToUser(updatedUser), isImportedUser: true };
      } else {
        // Username exists but is NOT an imported user - it's already taken
        console.log(`[Register] ❌ Username ${normalizedUsername} already exists with active account`);
        console.log(`[Register] User details:`, {
          email: existingUsername.email,
          password_hash_type: existingUsername.password_hash?.substring(0, 20),
        });
        throw new Error('Username already taken. If this is your account, please sign in instead.');
      }
    }

    // Hash password
    const passwordHash = await hashPassword(user.password);

    // Create new user in database (normalize email and username)
    const { data: dbUser, error } = await supabase
      .from('users')
      .insert({
        username: normalizedUsername,
        email: normalizedEmail,
        password_hash: passwordHash,
      })
      .select()
      .single();

    if (error) {
      console.error('Supabase registration error:', error);
      // Provide more helpful error messages
      if (error.code === '23505') {
        // Unique constraint violation
        if (error.message.includes('email')) {
          throw new Error('Email already registered');
        } else if (error.message.includes('username')) {
          throw new Error('Username already taken');
        }
      }
      throw new Error(`Failed to register user: ${error.message}`);
    }

    return { user: dbUserToUser(dbUser), isImportedUser: false };
  },

  async login(credentials: { email: string; password: string }): Promise<User> {
    if (!checkSupabaseConfig()) {
      throw new Error('Supabase is not configured. Please set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.');
    }

    // Normalize input - could be email or username
    const normalizedInput = credentials.email.trim().toLowerCase();
    const isEmail = normalizedInput.includes('@');

    let dbUser;
    let error;

    // Try email first if it looks like an email
    if (isEmail) {
      const { data, error: emailError } = await supabase
        .from('users')
        .select('*')
        .eq('email', normalizedInput)
        .single();
      
      dbUser = data;
      error = emailError;
    }

    // If email lookup failed or input doesn't look like email, try username
    if (!dbUser || error) {
      const { data: usernameData, error: usernameError } = await supabase
        .from('users')
        .select('*')
        .ilike('username', normalizedInput)
        .single();
      
      dbUser = usernameData;
      error = usernameError;
    }

    if (error || !dbUser) {
      console.error('Login error - user not found:', error);
      throw new Error('Invalid email/username or password');
    }

    // Verify password
    if (!dbUser.password_hash) {
      throw new Error('User account error');
    }

    const isValid = await verifyPassword(credentials.password, dbUser.password_hash);
    if (!isValid) {
      throw new Error('Invalid email or password');
    }

    // Set up Supabase auth session (we'll use custom auth since we're managing passwords ourselves)
    // For now, we'll store the user ID in a way that works with our system
    const user = dbUserToUser(dbUser);
    
    // Store user session in localStorage as fallback (will be replaced with proper auth)
    localStorage.setItem('current_user_id', user.id);
    
    return user;
  },

  async requestPasswordReset(emailOrUsername: string): Promise<string> {
    if (!checkSupabaseConfig()) {
      throw new Error('Supabase is not configured. Please set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.');
    }

    // Normalize input - could be email or username
    const normalizedInput = emailOrUsername.trim().toLowerCase();
    const isEmail = normalizedInput.includes('@');

    let dbUser;
    let error;

    // Try email first if it looks like an email
    if (isEmail) {
      const { data, error: emailError } = await supabase
        .from('users')
        .select('id, username, email')
        .eq('email', normalizedInput)
        .single();
      
      dbUser = data;
      error = emailError;
    }

    // If email lookup failed or input doesn't look like email, try username
    if (!dbUser || error) {
      const { data: usernameData, error: usernameError } = await supabase
        .from('users')
        .select('id, username, email')
        .ilike('username', normalizedInput)
        .single();
      
      dbUser = usernameData;
      error = usernameError;
    }

    if (error || !dbUser) {
      // Don't reveal if user exists or not (security best practice)
      throw new Error('If an account exists with that email or username, a reset token has been generated.');
    }

    // Generate reset token (32 character random string)
    const resetToken = Array.from(crypto.getRandomValues(new Uint8Array(16)))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('');

    // Set expiration to 1 hour from now
    const expiresAt = new Date();
    expiresAt.setHours(expiresAt.getHours() + 1);

    // Store reset token in database
    const { error: updateError } = await supabase
      .from('users')
      .update({
        reset_token: resetToken,
        reset_token_expires_at: expiresAt.toISOString(),
      })
      .eq('id', dbUser.id);

    if (updateError) {
      console.error('[requestPasswordReset] Error storing reset token:', updateError);
      throw new Error('Failed to generate reset token. Please try again.');
    }

    // Return token (in production, this would be sent via email)
    return resetToken;
  },

  async resetPassword(token: string, newPassword: string): Promise<void> {
    if (!checkSupabaseConfig()) {
      throw new Error('Supabase is not configured. Please set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY.');
    }

    if (!token || !newPassword) {
      throw new Error('Reset token and new password are required.');
    }

    if (newPassword.length < 6) {
      throw new Error('Password must be at least 6 characters long.');
    }

    // Find user with this reset token
    const { data: dbUser, error: findError } = await supabase
      .from('users')
      .select('id, reset_token, reset_token_expires_at')
      .eq('reset_token', token)
      .single();

    if (findError || !dbUser) {
      throw new Error('Invalid or expired reset token. Please request a new one.');
    }

    // Check if token has expired
    if (dbUser.reset_token_expires_at) {
      const expiresAt = new Date(dbUser.reset_token_expires_at);
      if (expiresAt < new Date()) {
        throw new Error('Reset token has expired. Please request a new one.');
      }
    }

    // Hash new password
    const passwordHash = await hashPassword(newPassword);

    // Update password and clear reset token
    const { error: updateError } = await supabase
      .from('users')
      .update({
        password_hash: passwordHash,
        reset_token: null,
        reset_token_expires_at: null,
      })
      .eq('id', dbUser.id);

    if (updateError) {
      console.error('[resetPassword] Error updating password:', updateError);
      throw new Error('Failed to reset password. Please try again.');
    }
  },

  async getCurrentUser(): Promise<User | null> {
    if (!checkSupabaseConfig()) {
      return null;
    }

    const userId = localStorage.getItem('current_user_id');
    if (!userId) {
      console.log('[getCurrentUser] No user ID in localStorage');
      return null;
    }

    // Validate that the user ID is a UUID (Supabase uses UUIDs)
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!uuidRegex.test(userId)) {
      // If the user ID is not a UUID, it's from localStorage mode - clear it
      console.log('[getCurrentUser] Invalid user ID format (not a UUID):', userId);
      localStorage.removeItem('current_user_id');
      return null;
    }

    try {
      const { data: dbUser, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        console.error('[getCurrentUser] Error fetching user:', error);
        // Only clear session if user truly doesn't exist (PGRST116 = no rows)
        // Don't clear on network errors or other issues
        if (error.code === 'PGRST116') {
          // No rows returned - user doesn't exist
          console.log('[getCurrentUser] User not found in Supabase (PGRST116), clearing stored user ID');
          localStorage.removeItem('current_user_id');
        } else {
          // Network error or other issue - keep the session, just return null
          // The user might still be valid, just couldn't fetch right now
          console.warn('[getCurrentUser] Error fetching user (non-fatal):', error.code, error.message);
        }
        return null;
      }

      if (!dbUser) {
        console.log('[getCurrentUser] No user data returned (but no error)');
        // This shouldn't happen, but if it does, clear the session
        localStorage.removeItem('current_user_id');
        return null;
      }

      console.log('[getCurrentUser] Successfully loaded user:', dbUser.username);
      return dbUserToUser(dbUser);
    } catch (err) {
      // Network errors or other exceptions - don't clear session
      // The user might still be valid, just couldn't fetch right now
      console.error('[getCurrentUser] Exception (non-fatal):', err);
      return null;
    }
  },

  async updateUser(userId: string, updates: Partial<User>): Promise<User> {
    if (!checkSupabaseConfig()) {
      throw new Error('Supabase is not configured.');
    }

    const dbUpdates: Partial<DatabaseUser> = {};
    if (updates.username) dbUpdates.username = updates.username;
    if (updates.email) dbUpdates.email = updates.email;
    if (updates.avatar_url) dbUpdates.avatar_url = updates.avatar_url;
    if (updates.favorite_genre !== undefined) dbUpdates.favorite_genre = updates.favorite_genre;
    if (updates.total_submissions !== undefined) dbUpdates.total_submissions = updates.total_submissions;
    if (updates.isAdmin !== undefined) dbUpdates.is_admin = updates.isAdmin;

    const { data: dbUser, error } = await supabase
      .from('users')
      .update(dbUpdates)
      .eq('id', userId)
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to update user: ${error.message}`);
    }

    return dbUserToUser(dbUser);
  },

  async getUsers(): Promise<{ data: User[] | null; error: any }> {
    if (!checkSupabaseConfig()) {
      return { data: null, error: new Error('Supabase is not configured.') };
    }

    const { data: dbUsers, error } = await supabase
      .from('users')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      return { data: null, error };
    }

    return { data: dbUsers?.map(dbUserToUser) || [], error: null };
  },
};

// League API
export const leagueAPI = {
  /**
   * Create a basic league (just name and description)
   * Used when creating a new league inline during season creation
   */
  async createBasicLeague(payload: {
    name: string;
    description?: string;
    created_by: string;
    max_players?: number;
  }): Promise<League> {
    if (!checkSupabaseConfig()) {
      throw new Error('Supabase is not configured.');
    }

    // Generate invite code
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let inviteCode = '';
    let attempts = 0;
    while (attempts < 10) {
      inviteCode = '';
      for (let i = 0; i < 6; i++) {
        inviteCode += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      inviteCode = inviteCode.toUpperCase();
      
      const { data: existing } = await supabase
        .from('leagues')
        .select('id')
        .eq('invite_code', inviteCode)
        .maybeSingle();
      
      if (!existing) {
        break;
      }
      attempts++;
    }

    if (!inviteCode) {
      throw new Error('Failed to generate unique invite code');
    }

    // Create league with defaults
    const { data: dbLeague, error: leagueError } = await supabase
      .from('leagues')
      .insert({
        name: payload.name,
        description: payload.description || '',
        invite_code: inviteCode,
        created_by: payload.created_by,
        max_players: payload.max_players || 12, // Default, can be changed later
        points_per_round: 10, // Default, can be changed later
        scoring: {}, // Default, can be changed per season
        require_spotify: false,
        is_public: false,
      })
      .select()
      .single();

    if (leagueError) {
      throw new Error(`Failed to create league: ${leagueError.message}`);
    }

    // Add creator as member and admin (trigger should handle this, but ensure it)
    await supabase
      .from('league_members')
      .insert({
        league_id: dbLeague.id,
        user_id: payload.created_by,
        is_admin: true,
      })
      .select();

    // Create activity
    await supabase
      .from('activities')
      .insert({
        type: 'league_created',
        message: `League "${dbLeague.name}" was created`,
        user_id: payload.created_by,
        league_id: dbLeague.id,
      });

    return dbLeagueToLeague(dbLeague, 1);
  },

  async createLeague(payload: {
    league: Omit<League, 'id' | 'created_at' | 'member_count' | 'current_round'>;
    rounds: Array<Omit<Round, 'id' | 'league_id' | 'created_at'>>;
  }): Promise<League> {
    if (!checkSupabaseConfig()) {
      throw new Error('Supabase is not configured.');
    }

    // Generate invite code - use provided code or generate one
    let inviteCode = payload.league.invite_code;
    if (!inviteCode) {
      // Try to use the database function, fallback to client-side generation
      const { data: inviteCodeData, error: inviteError } = await supabase.rpc('generate_invite_code');
      if (inviteError || !inviteCodeData) {
        // Fallback if function doesn't exist
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
        let code = '';
        // Keep generating until we find a unique one
        let attempts = 0;
        while (attempts < 10) {
          code = '';
          for (let i = 0; i < 6; i++) {
            code += chars.charAt(Math.floor(Math.random() * chars.length));
          }
          code = code.toUpperCase();
          
          // Check if code exists
          const { data: existing, error: checkError } = await supabase
            .from('leagues')
            .select('id')
            .eq('invite_code', code)
            .maybeSingle();
          
          // If code doesn't exist (error means not found, which is good), use it
          if (!existing || checkError) {
            inviteCode = code;
            break;
          }
          attempts++;
        }
        if (!inviteCode) {
          throw new Error('Failed to generate unique invite code');
        }
      } else {
        inviteCode = inviteCodeData;
      }
    }
    payload.league.invite_code = inviteCode.toUpperCase();

    // Create league
    const { data: dbLeague, error: leagueError } = await supabase
      .from('leagues')
      .insert({
        name: payload.league.name,
        description: payload.league.description,
        invite_code: payload.league.invite_code.toUpperCase(),
        created_by: payload.league.created_by,
        max_players: payload.league.max_players,
        points_per_round: payload.league.points_per_round,
        scoring: payload.league.scoring,
        require_spotify: payload.league.require_spotify,
        is_public: payload.league.is_public,
      })
      .select()
      .single();

    if (leagueError) {
      throw new Error(`Failed to create league: ${leagueError.message}`);
    }

    // Create rounds
    const dbRounds = payload.rounds.map((round, index) => ({
      league_id: dbLeague.id,
      round_number: round.round_number || index + 1,
      theme: round.theme,
      description: round.description,
      season: round.season,
      submission_deadline: round.submission_deadline,
      voting_deadline: round.voting_deadline,
      status: round.status || 'submitting',
    }));

    const { data: createdRounds, error: roundsError } = await supabase
      .from('rounds')
      .insert(dbRounds)
      .select();

    if (roundsError) {
      throw new Error(`Failed to create rounds: ${roundsError.message}`);
    }

    // Add creator as member and admin
    await supabase
      .from('league_members')
      .insert({
        league_id: dbLeague.id,
        user_id: payload.league.created_by,
        is_admin: true, // Creator is automatically admin
      });

    // Create activity
    await supabase
      .from('activities')
      .insert({
        type: 'league_created',
        message: `League "${dbLeague.name}" was created`,
        user_id: payload.league.created_by,
        league_id: dbLeague.id,
      });

    // Get member count
    const { count } = await supabase
      .from('league_members')
      .select('*', { count: 'exact', head: true })
      .eq('league_id', dbLeague.id);

    const currentRound = createdRounds && createdRounds[0] ? dbRoundToRound(createdRounds[0]) : undefined;

    return dbLeagueToLeague(dbLeague, count || 1, currentRound);
  },

  async getLeague(leagueId: string): Promise<League | null> {
    if (!checkSupabaseConfig()) {
      return null;
    }

    const { data: dbLeague, error } = await supabase
      .from('leagues')
      .select('*')
      .eq('id', leagueId)
      .single();

    if (error || !dbLeague) {
      return null;
    }

    // Get member count
    const { count } = await supabase
      .from('league_members')
      .select('*', { count: 'exact', head: true })
      .eq('league_id', leagueId);

    const memberCount = count || 0;

    // Get current round (first round with status 'submitting' or 'voting', or first round)
    const { data: rounds } = await supabase
      .from('rounds')
      .select('*')
      .eq('league_id', leagueId)
      .order('round_number', { ascending: true });

    // Check and transition any submitting rounds that should be voting
    const updatedRounds = await Promise.all(
      (rounds || []).map(async (round) => {
        if (round.status === 'submitting') {
          const updated = await roundAPI.checkAndTransitionRoundStatus(round.id, dbLeague.max_players);
          return updated || dbRoundToRound(round);
        }
        return dbRoundToRound(round);
      })
    );

    const currentRound = updatedRounds && updatedRounds.length > 0 
      ? updatedRounds.find(r => r.status === 'submitting' || r.status === 'voting') || updatedRounds[0]
      : undefined;

    return dbLeagueToLeague(dbLeague, memberCount, currentRound);
  },

  async updateLeague(leagueId: string, updates: Partial<{
    name?: string;
    description?: string;
    max_players?: number;
    points_per_round?: number;
    scoring?: LeagueScoring;
    require_spotify?: boolean;
    is_public?: boolean;
    current_playlist_id?: string | null;
  }>): Promise<League> {
    if (!checkSupabaseConfig()) {
      throw new Error('Supabase is not configured.');
    }

    const { data: dbLeague, error } = await supabase
      .from('leagues')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', leagueId)
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to update league: ${error.message}`);
    }

    // Re-fetch member count and current round to return full League object
    const { count } = await supabase
      .from('league_members')
      .select('*', { count: 'exact', head: true })
      .eq('league_id', leagueId);
    const memberCount = count || 0;

    const { data: rounds } = await supabase
      .from('rounds')
      .select('*')
      .eq('league_id', leagueId)
      .order('round_number', { ascending: true });

    const currentRound = rounds && rounds.length > 0
      ? rounds.find(r => r.status === 'submitting' || r.status === 'voting') || rounds[0]
      : undefined;

    return dbLeagueToLeague(dbLeague, memberCount, currentRound ? dbRoundToRound(currentRound) : undefined);
  },

  async findLeagueByInviteCode(code: string): Promise<League | null> {
    if (!checkSupabaseConfig()) {
      return null;
    }

    const normalizedCode = code.toUpperCase().trim();
    console.log('[findLeagueByInviteCode] Searching for invite code:', normalizedCode);

    // Add timeout to prevent hanging on slow networks (especially mobile)
    const timeoutPromise = new Promise<null>((resolve) => {
      setTimeout(() => resolve(null), 10000); // 10 second timeout
    });

    const queryPromise = (async () => {
      // Use maybeSingle() instead of single() to handle no results gracefully
      // This prevents 406 errors when the invite code doesn't exist
      const { data: dbLeague, error } = await supabase
        .from('leagues')
        .select('*')
        .eq('invite_code', normalizedCode)
        .maybeSingle();

      if (error) {
        console.error('[findLeagueByInviteCode] Error finding league by invite code:', error);
        console.error('[findLeagueByInviteCode] Error details:', {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint,
        });
        // Check if it's a 406 error (Not Acceptable)
        if (error.code === 'PGRST301' || error.message?.includes('406')) {
          console.warn('406 error when querying invite code - this might be a Supabase configuration issue');
        }
        return null;
      }

      if (!dbLeague) {
        // No league found with this invite code
        console.log('[findLeagueByInviteCode] No league found with invite code:', normalizedCode);
        
        // Try a case-insensitive search as fallback
        const { data: caseInsensitiveLeague } = await supabase
          .from('leagues')
          .select('*')
          .ilike('invite_code', normalizedCode)
          .maybeSingle();
        
        if (caseInsensitiveLeague) {
          console.log('[findLeagueByInviteCode] Found league with case-insensitive search:', caseInsensitiveLeague);
          // Use the found league
          const { count } = await supabase
            .from('league_members')
            .select('*', { count: 'exact', head: true })
            .eq('league_id', caseInsensitiveLeague.id);

          const { data: rounds } = await supabase
            .from('rounds')
            .select('*')
            .eq('league_id', caseInsensitiveLeague.id)
            .order('round_number', { ascending: true });

          const currentRound = rounds && rounds.length > 0 
            ? rounds.find(r => r.status === 'submitting' || r.status === 'voting') || rounds[0]
            : undefined;

          return dbLeagueToLeague(caseInsensitiveLeague, count || 0, currentRound ? dbRoundToRound(currentRound) : undefined);
        }
        
        return null;
      }

      console.log('[findLeagueByInviteCode] Found league:', dbLeague);
    
      // Get member count
      const { count } = await supabase
        .from('league_members')
        .select('*', { count: 'exact', head: true })
        .eq('league_id', dbLeague.id);

      // Get current round
      const { data: rounds } = await supabase
        .from('rounds')
        .select('*')
        .eq('league_id', dbLeague.id)
        .order('round_number', { ascending: true });

      const currentRound = rounds && rounds.length > 0 
        ? rounds.find(r => r.status === 'submitting' || r.status === 'voting') || rounds[0]
        : undefined;

      return dbLeagueToLeague(dbLeague, count || 0, currentRound ? dbRoundToRound(currentRound) : undefined);
    })();

    // Race between query and timeout - return null if timeout wins
    try {
      const result = await Promise.race([
        queryPromise,
        timeoutPromise.then(() => {
          console.warn('[findLeagueByInviteCode] Query timed out after 10 seconds');
          throw new Error('Request timed out. Please check your connection and try again.');
        })
      ]);
      return result;
    } catch (error) {
      if (error instanceof Error && error.message.includes('timed out')) {
        return null;
      }
      throw error;
    }
  },

  async joinLeague(leagueId: string, userId: string): Promise<void> {
    if (!checkSupabaseConfig()) {
      throw new Error('Supabase is not configured.');
    }

    // Check if league exists and is not full
    const league = await this.getLeague(leagueId);
    if (!league) {
      throw new Error('League not found');
    }

    if (league.member_count >= league.max_players) {
      throw new Error('League is full');
    }

    // Check if user is already a member
    const { data: existingMember } = await supabase
      .from('league_members')
      .select('*')
      .eq('league_id', leagueId)
      .eq('user_id', userId)
      .single();

    if (existingMember) {
      throw new Error('Already a member of this league');
    }

    // Add user to league
    const { error } = await supabase
      .from('league_members')
      .insert({
        league_id: leagueId,
        user_id: userId,
      });

    if (error) {
      throw new Error(`Failed to join league: ${error.message}`);
    }

    // Create activity
    await supabase
      .from('activities')
      .insert({
        type: 'join',
        message: `Joined league "${league.name}"`,
        user_id: userId,
        league_id: leagueId,
      });
  },

  /**
   * Get leagues where user is an admin
   */

  /**
   * Add a member to a league
   */
  async addMember(leagueId: string, userId: string, isAdmin: boolean = false): Promise<void> {
    if (!checkSupabaseConfig()) {
      throw new Error('Supabase is not configured.');
    }

    const { error } = await supabase
      .from('league_members')
      .insert({
        league_id: leagueId,
        user_id: userId,
        is_admin: isAdmin,
      });

    if (error) {
      // If user is already a member, that's okay
      if (error.code !== '23505') {
        throw new Error(`Failed to add member: ${error.message}`);
      }
    }
  },

  async getLeaguesByAdmin(userId: string): Promise<League[]> {
    if (!checkSupabaseConfig()) {
      return [];
    }

    const { data: adminMemberships, error } = await supabase
      .from('league_members')
      .select('league_id')
      .eq('user_id', userId)
      .eq('is_admin', true);

    if (error || !adminMemberships || adminMemberships.length === 0) {
      return [];
    }

    const leagueIds = adminMemberships.map(m => m.league_id);
    const { data: leagues, error: leaguesError } = await supabase
      .from('leagues')
      .select('*')
      .in('id', leagueIds);

    if (leaguesError || !leagues) {
      return [];
    }

    // Get member counts for each league
    const leaguesWithCounts = await Promise.all(
      leagues.map(async (league) => {
        const { count } = await supabase
          .from('league_members')
          .select('*', { count: 'exact', head: true })
          .eq('league_id', league.id);
        return dbLeagueToLeague(league, count || 0);
      })
    );

    return leaguesWithCounts;
  },

  async getAllLeagues(): Promise<League[]> {
    if (!checkSupabaseConfig()) {
      return [];
    }

    const { data: dbLeagues, error } = await supabase
      .from('leagues')
      .select('*')
      .order('name', { ascending: true });

    if (error || !dbLeagues) {
      console.error('[getAllLeagues] Error fetching leagues:', error);
      return [];
    }

    // Get member counts for each league
    const leaguesWithCounts = await Promise.all(
      dbLeagues.map(async (dbLeague) => {
        const { count } = await supabase
          .from('league_members')
          .select('*', { count: 'exact', head: true })
          .eq('league_id', dbLeague.id);
        
        return dbLeagueToLeague(dbLeague, count || 0);
      })
    );

    return leaguesWithCounts;
  },

  async getLeaguesByUser(userId: string): Promise<League[]> {
    if (!checkSupabaseConfig()) {
      console.warn('[API] Supabase not configured, returning empty leagues array');
      return [];
    }

    console.log('[API] getLeaguesByUser called for userId:', userId);

    // Get leagues where user is a member or creator
    const { data: memberships, error: membershipsError } = await supabase
      .from('league_members')
      .select('league_id')
      .eq('user_id', userId);

    if (membershipsError) {
      console.error('[API] Error fetching league memberships:', membershipsError);
    }

    const leagueIds = memberships?.map(m => m.league_id) || [];
    console.log('[API] Found league memberships:', leagueIds.length);

    // Also get leagues created by user
    const { data: createdLeagues, error: createdError } = await supabase
      .from('leagues')
      .select('id')
      .eq('created_by', userId);

    if (createdError) {
      console.error('[API] Error fetching created leagues:', createdError);
    }

    const allLeagueIds = [
      ...leagueIds,
      ...(createdLeagues?.map(l => l.id) || []),
    ];

    console.log('[API] Total league IDs (memberships + created):', allLeagueIds.length);

    if (allLeagueIds.length === 0) {
      console.log('[API] No leagues found for user');
      return [];
    }

    // Get unique league IDs
    const uniqueLeagueIds = [...new Set(allLeagueIds)];
    console.log('[API] Unique league IDs:', uniqueLeagueIds.length);

    // Get leagues
    const { data: dbLeagues, error: leaguesError } = await supabase
      .from('leagues')
      .select('*')
      .in('id', uniqueLeagueIds);

    if (leaguesError) {
      console.error('[API] Error fetching leagues:', leaguesError);
      return [];
    }

    if (!dbLeagues) {
      return [];
    }

    // Get member counts and current rounds for each league
    const leagues = await Promise.all(
      dbLeagues.map(async (dbLeague) => {
        const { count } = await supabase
          .from('league_members')
          .select('*', { count: 'exact', head: true })
          .eq('league_id', dbLeague.id);

        const { data: rounds } = await supabase
          .from('rounds')
          .select('*')
          .eq('league_id', dbLeague.id)
          .order('round_number', { ascending: true });

        const currentRound = rounds && rounds.length > 0 
          ? rounds.find(r => r.status === 'submitting' || r.status === 'voting') || rounds[0]
          : undefined;

        return dbLeagueToLeague(dbLeague, count || 0, currentRound ? dbRoundToRound(currentRound) : undefined);
      })
    );

    return leagues;
  },

  async getPublicLeagues(limit: number = 10): Promise<League[]> {
    if (!checkSupabaseConfig()) {
      return [];
    }

    const { data: dbLeagues } = await supabase
      .from('leagues')
      .select('*')
      .eq('is_public', true)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (!dbLeagues) {
      return [];
    }

    // Get member counts and current rounds
    const leagues = await Promise.all(
      dbLeagues.map(async (dbLeague) => {
        const { count } = await supabase
          .from('league_members')
          .select('*', { count: 'exact', head: true })
          .eq('league_id', dbLeague.id);

        const { data: rounds } = await supabase
          .from('rounds')
          .select('*')
          .eq('league_id', dbLeague.id)
          .order('round_number', { ascending: true });

        const currentRound = rounds && rounds.length > 0 
          ? rounds.find(r => r.status === 'submitting' || r.status === 'voting') || rounds[0]
          : undefined;

        return dbLeagueToLeague(dbLeague, count || 0, currentRound ? dbRoundToRound(currentRound) : undefined);
      })
    );

    return leagues;
  },
};

// Round API
export const roundAPI = {
  // Helper function to check if round should transition to voting
  async checkAndTransitionRoundStatus(roundId: string, leagueMaxPlayers: number): Promise<Round | null> {
    if (!checkSupabaseConfig()) {
      return null;
    }

    const { data: dbRound, error: fetchError } = await supabase
      .from('rounds')
      .select('*')
      .eq('id', roundId)
      .single();

    if (fetchError || !dbRound || dbRound.status !== 'submitting') {
      return dbRound ? dbRoundToRound(dbRound) : null;
    }

    // Check if submissions are locked (deadline passed OR all max_players have submitted)
    const deadline = new Date(dbRound.submission_deadline);
    const now = new Date();
    const isDeadlinePassed = now > deadline;

    // IMPORTANT: If deadline passed but status is still 'submitting', 
    // this means the round was manually reopened by admin.
    // NEVER auto-transition manually reopened rounds - admin must close them manually.
    if (isDeadlinePassed && dbRound.status === 'submitting') {
      console.log(`[Round Transition] Round ${roundId} was manually reopened (deadline passed but status is submitting). Skipping auto-transition.`);
      return dbRoundToRound(dbRound);
    }

    // Get submission count
    const { count: submissionCount } = await supabase
      .from('submissions')
      .select('*', { count: 'exact', head: true })
      .eq('round_id', roundId);

    // Check against max_players, not current member_count
    const allPlayersSubmitted = leagueMaxPlayers > 0 && (submissionCount || 0) >= leagueMaxPlayers;

    // Only auto-transition if deadline hasn't passed yet AND all players have submitted
    // (Normal flow - not a manually reopened round)
    if (!isDeadlinePassed && allPlayersSubmitted) {
      // Run research workflow before transitioning (find YouTube links, gather fun facts)
      try {
        console.log(`[Round Transition] Running research workflow for round ${roundId}`);
        const researchResults = await funFactsAPI.runResearchWorkflow(roundId);
        console.log(`[Round Transition] Research workflow completed:`, researchResults);
      } catch (error) {
        // Don't block transition if research workflow fails
        console.error(`[Round Transition] Research workflow failed (continuing with transition):`, error);
      }

      const { data: updatedRound, error: updateError } = await supabase
        .from('rounds')
        .update({ status: 'voting' })
        .eq('id', roundId)
        .select()
        .single();

      if (updateError) {
        console.error('Failed to transition round to voting:', updateError);
        return dbRoundToRound(dbRound);
      }

      return dbRoundToRound(updatedRound);
    }

    return dbRoundToRound(dbRound);
  },

  async getRound(roundId: string): Promise<Round | null> {
    if (!checkSupabaseConfig()) {
      return null;
    }

    const { data: dbRound, error } = await supabase
      .from('rounds')
      .select('*, seasons:seasons!rounds_season_id_fkey(name)')
      .eq('id', roundId)
      .single();

    if (error || !dbRound) {
      return null;
    }

    return dbRoundToRound(dbRound);
  },

  // Admin functions to manually change round status
  async closeSubmissions(roundId: string): Promise<Round> {
    if (!checkSupabaseConfig()) {
      throw new Error('Supabase is not configured.');
    }

    const { data: updatedRound, error } = await supabase
      .from('rounds')
      .update({ status: 'voting' })
      .eq('id', roundId)
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to close submissions: ${error.message}`);
    }

    return dbRoundToRound(updatedRound);
  },

  async reopenSubmissions(roundId: string): Promise<Round> {
    if (!checkSupabaseConfig()) {
      throw new Error('Supabase is not configured.');
    }

    const { data: updatedRound, error } = await supabase
      .from('rounds')
      .update({ status: 'submitting' })
      .eq('id', roundId)
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to reopen submissions: ${error.message}`);
    }

    return dbRoundToRound(updatedRound);
  },

  async closeVoting(roundId: string): Promise<Round> {
    if (!checkSupabaseConfig()) {
      throw new Error('Supabase is not configured.');
    }

    const { data: updatedRound, error } = await supabase
      .from('rounds')
      .update({ status: 'completed' })
      .eq('id', roundId)
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to close voting: ${error.message}`);
    }

    return dbRoundToRound(updatedRound);
  },

  async reopenVoting(roundId: string): Promise<Round> {
    if (!checkSupabaseConfig()) {
      throw new Error('Supabase is not configured.');
    }

    const { data: updatedRound, error } = await supabase
      .from('rounds')
      .update({ status: 'voting' })
      .eq('id', roundId)
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to reopen voting: ${error.message}`);
    }

    return dbRoundToRound(updatedRound);
  },

  async advanceToNextRound(roundId: string): Promise<Round | null> {
    if (!checkSupabaseConfig()) {
      throw new Error('Supabase is not configured.');
    }

    // Get current round
    const { data: currentRound, error: fetchError } = await supabase
      .from('rounds')
      .select('*')
      .eq('id', roundId)
      .single();

    if (fetchError || !currentRound) {
      throw new Error(`Failed to fetch round: ${fetchError?.message}`);
    }

    // Close current round
    const { data: closedRound, error: closeError } = await supabase
      .from('rounds')
      .update({ status: 'completed' })
      .eq('id', roundId)
      .select()
      .single();

    if (closeError) {
      throw new Error(`Failed to close round: ${closeError.message}`);
    }

    // Find next round in same league
    const { data: nextRounds, error: nextError } = await supabase
      .from('rounds')
      .select('*')
      .eq('league_id', currentRound.league_id)
      .eq('round_number', currentRound.round_number + 1)
      .order('round_number', { ascending: true })
      .limit(1);

    if (nextError) {
      throw new Error(`Failed to find next round: ${nextError.message}`);
    }

    if (!nextRounds || nextRounds.length === 0) {
      // No next round exists
      return null;
    }

    // Open next round
    const nextRound = nextRounds[0];
    const { data: openedRound, error: openError } = await supabase
      .from('rounds')
      .update({ status: 'submitting' })
      .eq('id', nextRound.id)
      .select()
      .single();

    if (openError) {
      throw new Error(`Failed to open next round: ${openError.message}`);
    }

    return dbRoundToRound(openedRound);
  },

  async getRoundsByLeague(leagueId: string): Promise<Round[]> {
    if (!checkSupabaseConfig()) {
      return [];
    }

    const { data: dbRounds, error } = await supabase
      .from('rounds')
      .select('*, seasons:seasons!rounds_season_id_fkey(name)')
      .eq('league_id', leagueId)
      .order('round_number', { ascending: true });

    if (error || !dbRounds) {
      return [];
    }

    return dbRounds.map(dbRoundToRound);
  },

  /**
   * Create multiple rounds for a season
   */
  async createSeasonRounds(payload: {
    league_id: string;
    season_name: string;
    rounds: Array<Omit<Round, 'id' | 'league_id' | 'created_at'>>;
  }): Promise<Round[]> {
    if (!checkSupabaseConfig()) {
      throw new Error('Supabase is not configured.');
    }

    const dbRounds = payload.rounds.map((round, index) => ({
      league_id: payload.league_id,
      round_number: round.round_number || index + 1,
      theme: round.theme,
      description: round.description,
      season: payload.season_name,
      submission_deadline: round.submission_deadline,
      voting_deadline: round.voting_deadline,
      status: round.status || 'submitting',
    }));

    const { data: createdRounds, error: roundsError } = await supabase
      .from('rounds')
      .insert(dbRounds)
      .select();

    if (roundsError) {
      throw new Error(`Failed to create rounds: ${roundsError.message}`);
    }

    return createdRounds.map(dbRoundToRound);
  },

  async createRound(round: Omit<Round, 'id' | 'created_at'>): Promise<Round> {
    if (!checkSupabaseConfig()) {
      throw new Error('Supabase is not configured.');
    }

    const { data: dbRound, error } = await supabase
      .from('rounds')
      .insert({
        league_id: round.league_id,
        round_number: round.round_number,
        theme: round.theme,
        description: round.description,
        season: round.season,
        submission_deadline: round.submission_deadline,
        voting_deadline: round.voting_deadline,
        status: round.status || 'submitting',
      })
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to create round: ${error.message}`);
    }

    return dbRoundToRound(dbRound);
  },
};

// Season API
export const seasonAPI = {
  /**
   * Create a season with rounds
   */
  async createSeason(payload: {
    league_id: string;
    season_name: string;
    description?: string;
    created_by: string;
    max_players: number;
    scoring: LeagueScoring;
    require_spotify: boolean;
    rounds: Array<Omit<Round, 'id' | 'league_id' | 'created_at'>>;
  }): Promise<{ season: Season; rounds: Round[] }> {
    if (!checkSupabaseConfig()) {
      throw new Error('Supabase is not configured.');
    }

    // Generate invite code for season
    let inviteCode = '';
    let attempts = 0;
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    
    while (attempts < 10) {
      inviteCode = '';
      for (let i = 0; i < 6; i++) {
        inviteCode += chars.charAt(Math.floor(Math.random() * chars.length));
      }
      inviteCode = inviteCode.toUpperCase();
      
      // Check if code exists in seasons or leagues
      const { data: existingSeason } = await supabase
        .from('seasons')
        .select('id')
        .eq('invite_code', inviteCode)
        .maybeSingle();
      
      const { data: existingLeague } = await supabase
        .from('leagues')
        .select('id')
        .eq('invite_code', inviteCode)
        .maybeSingle();
      
      if (!existingSeason && !existingLeague) {
        break;
      }
      attempts++;
    }

    if (!inviteCode) {
      throw new Error('Failed to generate unique invite code');
    }

    // Calculate sort_index from season name
    const calculateSortIndex = (name: string): number => {
      const yearMatch = name.match(/\b(19|20)(\d{2})\b/);
      if (yearMatch) {
        const year = parseInt(yearMatch[1] + yearMatch[2], 10);
        const lowerName = name.toLowerCase();
        let seasonNum = 5; // Default
        if (lowerName.includes('spring')) seasonNum = 1;
        else if (lowerName.includes('summer')) seasonNum = 2;
        else if (lowerName.includes('fall') || lowerName.includes('autumn')) seasonNum = 3;
        else if (lowerName.includes('winter')) seasonNum = 4;
        return (year * 1000) + (seasonNum * 100);
      }
      // If no year, use current timestamp (will be normalized later)
      return Math.floor(Date.now() / (1000 * 60 * 60 * 24));
    };

    // Create season
    const { data: dbSeason, error: seasonError } = await supabase
      .from('seasons')
      .insert({
        league_id: payload.league_id,
        name: payload.season_name,
        description: payload.description,
        invite_code: inviteCode,
        created_by: payload.created_by,
        max_players: payload.max_players,
        points_per_round: payload.scoring.max_upvotes_per_player,
        scoring: payload.scoring,
        require_spotify: payload.require_spotify,
        status: 'upcoming',
        sort_index: calculateSortIndex(payload.season_name),
      })
      .select()
      .single();

    if (seasonError) {
      throw new Error(`Failed to create season: ${seasonError.message}`);
    }

    // Create rounds
    const dbRounds = payload.rounds.map((round, index) => ({
      league_id: payload.league_id,
      season_id: dbSeason.id,
      round_number: round.round_number || index + 1,
      theme: round.theme,
      description: round.description,
      season: payload.season_name,
      submission_deadline: round.submission_deadline,
      voting_deadline: round.voting_deadline,
      status: round.status || 'submitting',
    }));

    const { data: createdRounds, error: roundsError } = await supabase
      .from('rounds')
      .insert(dbRounds)
      .select();

    if (roundsError) {
      // Rollback season creation
      await supabase.from('seasons').delete().eq('id', dbSeason.id);
      throw new Error(`Failed to create rounds: ${roundsError.message}`);
    }

    return {
      season: dbSeasonToSeason(dbSeason),
      rounds: createdRounds.map(dbRoundToRound),
    };
  },

  /**
   * Find season by invite code
   */
  async findSeasonByInviteCode(code: string): Promise<Season | null> {
    if (!checkSupabaseConfig()) {
      return null;
    }

    const normalizedCode = code.toUpperCase().trim();

    const { data: dbSeason, error } = await supabase
      .from('seasons')
      .select('*, leagues!inner(*)')
      .eq('invite_code', normalizedCode)
      .maybeSingle();

    if (error || !dbSeason) {
      return null;
    }

    return dbSeasonToSeason(dbSeason);
  },

  /**
   * Get seasons by league ID
   */
  async getSeasonsByLeague(leagueId: string): Promise<Season[]> {
    if (!checkSupabaseConfig()) {
      return [];
    }

    const { data: dbSeasons, error } = await supabase
      .from('seasons')
      .select('*')
      .eq('league_id', leagueId)
      .order('sort_index', { ascending: false, nullsLast: true })
      .order('created_at', { ascending: false });

    if (error || !dbSeasons) {
      return [];
    }

    return dbSeasons.map(dbSeasonToSeason);
  },

  /**
   * Get all seasons for user's leagues
   */
  async getAllSeasonsForUser(userId: string): Promise<Season[]> {
    if (!checkSupabaseConfig()) {
      return [];
    }

    // Get user's league IDs
    const { data: memberships } = await supabase
      .from('league_members')
      .select('league_id')
      .eq('user_id', userId);

    const { data: createdLeagues } = await supabase
      .from('leagues')
      .select('id')
      .eq('created_by', userId);

    const leagueIds = [
      ...(memberships?.map(m => m.league_id) || []),
      ...(createdLeagues?.map(l => l.id) || []),
    ];

    if (leagueIds.length === 0) {
      return [];
    }

    const uniqueLeagueIds = [...new Set(leagueIds)];

    const { data: dbSeasons, error } = await supabase
      .from('seasons')
      .select('*')
      .in('league_id', uniqueLeagueIds)
      .order('sort_index', { ascending: false, nullsLast: true })
      .order('created_at', { ascending: false });

    if (error || !dbSeasons) {
      return [];
    }

    return dbSeasons.map(dbSeasonToSeason);
  },

  /**
   * Join a season (automatically adds user to parent league)
   */
  async joinSeason(seasonId: string, userId: string): Promise<void> {
    if (!checkSupabaseConfig()) {
      throw new Error('Supabase is not configured.');
    }

    // Get season to find league_id
    const { data: season, error: seasonError } = await supabase
      .from('seasons')
      .select('league_id, name, max_players')
      .eq('id', seasonId)
      .single();

    if (seasonError || !season) {
      throw new Error('Season not found');
    }

    // Check if user is already a member of the league
    const { data: existingMember } = await supabase
      .from('league_members')
      .select('id')
      .eq('league_id', season.league_id)
      .eq('user_id', userId)
      .maybeSingle();

    // Add user to league if not already a member
    if (!existingMember) {
      const { error: memberError } = await supabase
        .from('league_members')
        .insert({
          league_id: season.league_id,
          user_id: userId,
          is_admin: false,
        });

      if (memberError) {
        throw new Error(`Failed to join league: ${memberError.message}`);
      }

      // Create activity
      await supabase
        .from('activities')
        .insert({
          type: 'join',
          message: `Joined season "${season.name}"`,
          user_id: userId,
          league_id: season.league_id,
        });
    }
  },
};

// Submission API
export const submissionAPI = {
  async getSubmissionsByRound(roundId: string): Promise<Submission[]> {
    if (!checkSupabaseConfig()) {
      return [];
    }

    // Try with user join first (for usernames). If RLS blocks `users`, this may fail or return empty.
    const { data: dbSubmissions, error } = await supabase
      .from('submissions')
      .select(`
        *,
        users:user_id (
          username,
          avatar_url
        )
      `)
      .eq('round_id', roundId)
      .order('total_points', { ascending: false });

    if (error) {
      console.error('[getSubmissionsByRound] Error fetching submissions (with users join):', error);

      // Fallback: fetch submissions without joining users.
      // This ensures archive pages still show submissions even if `users` table is restricted by RLS.
      const { data: bareSubmissions, error: bareError } = await supabase
        .from('submissions')
        .select('*')
        .eq('round_id', roundId)
        .order('total_points', { ascending: false });

      if (bareError) {
        console.error('[getSubmissionsByRound] Error fetching submissions (no join):', bareError);
        return [];
      }

      if (!bareSubmissions || bareSubmissions.length === 0) {
        console.warn('[getSubmissionsByRound] No submissions found for round:', roundId);
        return [];
      }

      return bareSubmissions.map((dbSubmission) => dbSubmissionToSubmission(dbSubmission as any));
    }

    if (!dbSubmissions || dbSubmissions.length === 0) {
      console.warn('[getSubmissionsByRound] No submissions found for round:', roundId);
      return [];
    }

    return dbSubmissions.map((dbSubmission) => {
      const submission = dbSubmissionToSubmission(dbSubmission);
      // Add username from joined users table if available
      const usersData = (dbSubmission as any).users;
      if (usersData && typeof usersData === 'object' && !Array.isArray(usersData)) {
        submission.username = usersData.username;
      }
      return submission;
    });
  },

  async createSubmission(submission: Omit<Submission, 'id' | 'submitted_at' | 'total_points'>): Promise<Submission> {
    if (!checkSupabaseConfig()) {
      throw new Error('Supabase is not configured.');
    }

    const { data: dbSubmission, error } = await supabase
      .from('submissions')
      .insert({
        round_id: submission.round_id,
        user_id: submission.user_id,
        song_title: submission.song_title,
        artist_name: submission.artist_name,
        spotify_track_id: submission.spotify_track_id,
        spotify_url: submission.spotify_url,
        youtube_video_id: submission.youtube_video_id,
        youtube_url: submission.video_url,
        submitter_comment: submission.submitter_comment,
        fun_facts: submission.fun_facts,
        total_points: 0,
      })
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to create submission: ${error.message}`);
    }

    // Update user's total submissions (increment by 1)
    const { data: user } = await supabase
      .from('users')
      .select('total_submissions')
      .eq('id', submission.user_id)
      .single();
    
    if (user) {
      await supabase
        .from('users')
        .update({ total_submissions: (user.total_submissions || 0) + 1 })
        .eq('id', submission.user_id);
    }

    // Create activity
    await supabase
      .from('activities')
      .insert({
        type: 'submission',
        message: `Submitted "${submission.song_title}" by ${submission.artist_name}`,
        user_id: submission.user_id,
        round_id: submission.round_id,
      });

    // Check if round should transition to voting (all members submitted)
    // Get league member count
    const { data: roundData } = await supabase
      .from('rounds')
      .select('league_id')
      .eq('id', submission.round_id)
      .single();

    if (roundData) {
      // Get league to access max_players
      const { data: leagueData } = await supabase
        .from('leagues')
        .select('max_players')
        .eq('id', roundData.league_id)
        .single();

      if (leagueData) {
        // Check and transition if needed
        await roundAPI.checkAndTransitionRoundStatus(submission.round_id, leagueData.max_players);
      }
    }

    return dbSubmissionToSubmission(dbSubmission);
  },

  async updateSubmission(submissionId: string, submission: Partial<Omit<Submission, 'id' | 'submitted_at' | 'total_points'>>): Promise<Submission> {
    if (!checkSupabaseConfig()) {
      throw new Error('Supabase is not configured.');
    }

    const updateData: any = {};
    if (submission.song_title !== undefined) updateData.song_title = submission.song_title;
    if (submission.artist_name !== undefined) updateData.artist_name = submission.artist_name;
    if (submission.spotify_track_id !== undefined) updateData.spotify_track_id = submission.spotify_track_id;
    if (submission.spotify_url !== undefined) updateData.spotify_url = submission.spotify_url;
    if (submission.youtube_video_id !== undefined) updateData.youtube_video_id = submission.youtube_video_id;
    if (submission.video_url !== undefined) updateData.youtube_url = submission.video_url;
    if (submission.submitter_comment !== undefined) updateData.submitter_comment = submission.submitter_comment;
    if (submission.fun_facts !== undefined) updateData.fun_facts = submission.fun_facts;
    // Note: video_thumbnail is not stored in the database - it's computed from youtube_video_id or spotify_album_image on the frontend

    const { data: dbSubmission, error } = await supabase
      .from('submissions')
      .update(updateData)
      .eq('id', submissionId)
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to update submission: ${error.message}`);
    }

    return dbSubmissionToSubmission(dbSubmission);
  },
};

// Vote API
export const voteAPI = {
  async getVotesByRound(roundId: string): Promise<Vote[]> {
    if (!checkSupabaseConfig()) {
      return [];
    }

    // Fetch votes for this round
    const { data: dbVotes, error: votesError } = await supabase
      .from('votes')
      .select('*')
      .eq('round_id', roundId);

    if (votesError) {
      console.error('[getVotesByRound] Error fetching votes:', votesError);
      return [];
    }

    if (!dbVotes || dbVotes.length === 0) {
      console.log('[getVotesByRound] No votes found for round:', roundId);
      return [];
    }

    console.log('[getVotesByRound] Found', dbVotes.length, 'votes for round', roundId);

    // Get unique user IDs from votes (votes table has user_id column)
    const userIds = [...new Set(dbVotes.map(v => v.user_id).filter(Boolean))];
    
    console.log('[getVotesByRound] Unique user IDs:', userIds.length, userIds);
    
    if (userIds.length === 0) {
      console.warn('[getVotesByRound] No user IDs found in votes');
      // No users to fetch, return votes without usernames
      return dbVotes.map(dbVoteToVote);
    }

    // Fetch users in batch
    const { data: usersData, error: usersError } = await supabase
      .from('users')
      .select('id, username')
      .in('id', userIds);

    if (usersError) {
      console.error('[getVotesByRound] Error fetching users:', usersError);
    }

    console.log('[getVotesByRound] Fetched', usersData?.length || 0, 'users');

    // Create a map of user_id -> username for fast lookup
    const usersMap = new Map<string, string>();
    if (usersData && !usersError) {
      usersData.forEach(user => {
        if (user.id && user.username) {
          usersMap.set(user.id, user.username);
          console.log('[getVotesByRound] Mapped user:', user.id, '->', user.username);
        }
      });
    } else {
      console.warn('[getVotesByRound] No users data or error occurred');
    }

    // Combine votes with usernames
    const votesWithUsernames = dbVotes.map(dbVote => {
      const vote = dbVoteToVote(dbVote);
      const username = usersMap.get(dbVote.user_id);
      if (!username) {
        console.warn('[getVotesByRound] No username found for user_id:', dbVote.user_id, 'vote_id:', dbVote.id);
      }
      return {
        ...vote,
        username: username || undefined,
      };
    });

    console.log('[getVotesByRound] Returning', votesWithUsernames.length, 'votes with usernames');
    return votesWithUsernames;
  },

  async createVote(vote: Omit<Vote, 'id' | 'created_at'>): Promise<Vote> {
    if (!checkSupabaseConfig()) {
      throw new Error('Supabase is not configured.');
    }

    // Use upsert to update if vote already exists
    const { data: dbVote, error } = await supabase
      .from('votes')
      .upsert({
        round_id: vote.round_id,
        user_id: vote.voter_id, // Note: API uses user_id, Vote interface uses voter_id
        submission_id: vote.submission_id,
        points: vote.points,
        comment: vote.comment || null, // Include voter comment
        is_submitted: vote.is_submitted ?? false,
      }, {
        onConflict: 'round_id,user_id,submission_id',
      })
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to create vote: ${error.message}`);
    }

    // Update submission total points
    await this.updateSubmissionPoints(vote.submission_id);

    return dbVoteToVote(dbVote);
  },

  async updateSubmissionPoints(submissionId: string): Promise<void> {
    if (!checkSupabaseConfig()) {
      return;
    }

    // Get all submitted votes for this submission (only count is_submitted = true)
    const { data: votes } = await supabase
      .from('votes')
      .select('points')
      .eq('submission_id', submissionId)
      .eq('is_submitted', true);

    const totalPoints = votes?.reduce((sum, vote) => sum + vote.points, 0) || 0;

    // Update submission
    await supabase
      .from('submissions')
      .update({ total_points: totalPoints })
      .eq('id', submissionId);
  },
};

// Activity API
export const activityAPI = {
  async getRecentActivities(limit: number = 10, userId?: string, leagueId?: string): Promise<Activity[]> {
    if (!checkSupabaseConfig()) {
      return [];
    }

    let query = supabase
      .from('activities')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limit);

    if (userId) {
      query = query.eq('user_id', userId);
    }

    if (leagueId) {
      query = query.eq('league_id', leagueId);
    }

    const { data: dbActivities, error } = await query;

    if (error || !dbActivities) {
      return [];
    }

    return dbActivities.map(dbActivityToActivity);
  },
};

// Playlist Comments API
export const playlistCommentAPI = {
  async getComments(playlistId: string, playlistType: 'round' | 'league' | 'season' | 'curated' | 'personal'): Promise<PlaylistComment[]> {
    if (!checkSupabaseConfig()) {
      return [];
    }

    const { data: dbComments, error } = await supabase
      .from('playlist_comments')
      .select(`
        *,
        users:user_id (
          id,
          username
        )
      `)
      .eq('playlist_id', playlistId)
      .eq('playlist_type', playlistType)
      .order('created_at', { ascending: false });

    if (error || !dbComments) {
      return [];
    }

    return dbComments.map((comment: any) => ({
      id: comment.id,
      playlist_id: comment.playlist_id,
      playlist_type: comment.playlist_type,
      user_id: comment.user_id,
      username: comment.users?.username,
      comment: comment.comment,
      created_at: comment.created_at,
      updated_at: comment.updated_at,
    }));
  },

  async createComment(comment: Omit<PlaylistComment, 'id' | 'created_at' | 'updated_at' | 'username'>): Promise<PlaylistComment> {
    if (!checkSupabaseConfig()) {
      throw new Error('Supabase is not configured.');
    }

    const { data: dbComment, error } = await supabase
      .from('playlist_comments')
      .insert({
        playlist_id: comment.playlist_id,
        playlist_type: comment.playlist_type,
        user_id: comment.user_id,
        comment: comment.comment,
      })
      .select(`
        *,
        users:user_id (
          id,
          username
        )
      `)
      .single();

    if (error) {
      throw new Error(`Failed to create comment: ${error.message}`);
    }

    return {
      id: dbComment.id,
      playlist_id: dbComment.playlist_id,
      playlist_type: dbComment.playlist_type,
      user_id: dbComment.user_id,
      username: dbComment.users?.username,
      comment: dbComment.comment,
      created_at: dbComment.created_at,
      updated_at: dbComment.updated_at,
    };
  },

  async updateComment(commentId: string, comment: string): Promise<PlaylistComment> {
    if (!checkSupabaseConfig()) {
      throw new Error('Supabase is not configured.');
    }

    const { data: dbComment, error } = await supabase
      .from('playlist_comments')
      .update({ comment })
      .eq('id', commentId)
      .select(`
        *,
        users:user_id (
          id,
          username
        )
      `)
      .single();

    if (error) {
      throw new Error(`Failed to update comment: ${error.message}`);
    }

    return {
      id: dbComment.id,
      playlist_id: dbComment.playlist_id,
      playlist_type: dbComment.playlist_type,
      user_id: dbComment.user_id,
      username: dbComment.users?.username,
      comment: dbComment.comment,
      created_at: dbComment.created_at,
      updated_at: dbComment.updated_at,
    };
  },

  async deleteComment(commentId: string): Promise<void> {
    if (!checkSupabaseConfig()) {
      throw new Error('Supabase is not configured.');
    }

    const { error } = await supabase
      .from('playlist_comments')
      .delete()
      .eq('id', commentId);

    if (error) {
      throw new Error(`Failed to delete comment: ${error.message}`);
    }
  },
};

// Fun Facts API
export const funFactsAPI = {
  async updateSubmissionFunFacts(submissionId: string, funFacts: string[]): Promise<Submission> {
    if (!checkSupabaseConfig()) {
      throw new Error('Supabase is not configured.');
    }

    const { data: dbSubmission, error } = await supabase
      .from('submissions')
      .update({ fun_facts: funFacts })
      .eq('id', submissionId)
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to update fun facts: ${error.message}`);
    }

    return dbSubmissionToSubmission(dbSubmission);
  },

  async bulkUpdateFunFacts(updates: Array<{ submissionId: string; funFacts: string[] }>): Promise<void> {
    if (!checkSupabaseConfig()) {
      throw new Error('Supabase is not configured.');
    }

    // Update each submission
    const promises = updates.map(({ submissionId, funFacts }) =>
      supabase
        .from('submissions')
        .update({ fun_facts: funFacts })
        .eq('id', submissionId)
    );

    const results = await Promise.all(promises);
    
    // Check for errors
    const errors = results.filter(result => result.error);
    if (errors.length > 0) {
      throw new Error(`Failed to update ${errors.length} submission(s)`);
    }
  },

  /**
   * Research workflow: Find YouTube links and gather fun facts for submissions
   * This runs automatically when submissions close
   * 
   * @param roundId - The round ID to process
   * @returns Object with counts of updated submissions
   */
  async runResearchWorkflow(roundId: string): Promise<{ youtubeLinksFound: number; funFactsGathered: number }> {
    if (!checkSupabaseConfig()) {
      throw new Error('Supabase is not configured.');
    }

    console.log(`[Research Workflow] Invoking Edge Function for round ${roundId}`);

    try {
      // Call Supabase Edge Function to handle both YouTube links and fun facts
      const { data, error } = await supabase.functions.invoke('research-submissions', {
        body: { roundId },
      });

      if (error) {
        console.error(`[Research Workflow] Edge Function error:`, error);
        throw new Error(`Failed to run research workflow: ${error.message}`);
      }

      const result = data as { youtubeLinksFound: number; funFactsGathered: number; errors?: string[] };
      
      if (result.errors && result.errors.length > 0) {
        console.warn(`[Research Workflow] Completed with ${result.errors.length} errors:`, result.errors);
      }

      console.log(`[Research Workflow] Complete: ${result.youtubeLinksFound} YouTube links, ${result.funFactsGathered} fun facts`);
      
      return {
        youtubeLinksFound: result.youtubeLinksFound || 0,
        funFactsGathered: result.funFactsGathered || 0,
      };
    } catch (error) {
      console.error(`[Research Workflow] Failed to invoke Edge Function:`, error);
      // Don't throw - allow the round transition to continue even if research fails
      return { youtubeLinksFound: 0, funFactsGathered: 0 };
    }
  },

  /**
   * Gather fun facts for all submissions in a round using AI
   * This is a placeholder that should be integrated with an AI service
   * 
   * @param roundId - The round ID to gather fun facts for
   * @param aiPrompt - Optional custom prompt for AI generation
   * @returns Number of submissions updated
   */
  async gatherFunFactsForRound(roundId: string, aiPrompt?: string): Promise<number> {
    if (!checkSupabaseConfig()) {
      throw new Error('Supabase is not configured.');
    }

    // Get all submissions for the round
    const { data: submissions, error: fetchError } = await supabase
      .from('submissions')
      .select('id, song_title, artist_name')
      .eq('round_id', roundId);

    if (fetchError || !submissions || submissions.length === 0) {
      throw new Error(`No submissions found for round ${roundId}`);
    }

    console.log(`[Fun Facts] Gathering fun facts for ${submissions.length} submissions in round ${roundId}`);

    // TODO: Integrate with AI service (OpenAI, Anthropic, etc.)
    // For now, this is a placeholder that returns 0
    // The actual implementation should:
    // 1. For each submission, call AI service with prompt like:
    //    "Generate 3-5 interesting fun facts about the song '{song_title}' by '{artist_name}'"
    // 2. Parse the AI response into an array of fun fact strings
    // 3. Update the submission with the fun facts using updateSubmissionFunFacts
    
    const defaultPrompt = aiPrompt || 
      `Generate 3-5 interesting and engaging fun facts about the song and artist. 
       Focus on unique details, chart performance, cultural impact, behind-the-scenes stories, 
       or interesting trivia. Return as a JSON array of strings.`;

    // Placeholder: In a real implementation, you would:
    // 1. Call your AI service here
    // 2. Parse the response
    // 3. Update each submission
    
    throw new Error('AI fun facts gathering not yet implemented. Please use the import script for now.');
    
    // Example of what the implementation might look like:
    /*
    let updatedCount = 0;
    for (const submission of submissions) {
      try {
        const aiResponse = await callAIService({
          prompt: `${defaultPrompt}\n\nSong: "${submission.song_title}" by ${submission.artist_name}`,
        });
        
        const funFacts = parseAIResponse(aiResponse); // Should return string[]
        
        await this.updateSubmissionFunFacts(submission.id, funFacts);
        updatedCount++;
      } catch (error) {
        console.error(`Error gathering fun facts for submission ${submission.id}:`, error);
      }
    }
    
    return updatedCount;
    */
  },
};

// Helper function to generate Spotify playlist URL
export function generateSpotifyPlaylistUrl(tracks: Array<{ spotify_track_id?: string; spotify_url?: string }>): string | null {
  const spotifyTrackIds = tracks
    .map(track => {
      if (track.spotify_track_id) {
        return track.spotify_track_id;
      }
      // Extract track ID from Spotify URL if available
      if (track.spotify_url) {
        const match = track.spotify_url.match(/track\/([a-zA-Z0-9]+)/);
        if (match) {
          return match[1];
        }
      }
      return null;
    })
    .filter((id): id is string => id !== null);

  if (spotifyTrackIds.length === 0) {
    return null;
  }

  // Spotify playlist URL format: https://open.spotify.com/playlist/{playlist_id}
  // For creating a playlist with tracks, we can use: https://open.spotify.com/?uri=spotify:trackset:PlaylistName:{track_ids}
  // Or better: https://open.spotify.com/playlist/{playlist_id}?si=... (requires creating a playlist)
  
  // For now, we'll create a search URL that users can use to find the tracks
  // Or we can create a comma-separated list of track IDs for the Spotify Web API
  const trackIdsParam = spotifyTrackIds.join(',');
  
  // Return a URL that opens Spotify with the tracks
  // Note: This requires the user to have Spotify installed or use the web player
  return `https://open.spotify.com/?uri=spotify:trackset:Playlist:${trackIdsParam}`;
}

// Admin API for resetting data
export const adminAPI = {
  async resetAllData(): Promise<void> {
    if (!checkSupabaseConfig()) {
      throw new Error('Supabase is not configured.');
    }

    // Use RPC function if available, otherwise fallback to individual deletes
    const { error } = await supabase.rpc('reset_all_data');
    if (error) {
      // Fallback: try individual deletes
      console.warn('RPC function not available, using fallback:', error);
      await this.resetAllDataFallback();
    }
  },

  async resetAllDataFallback(): Promise<void> {
    // Fallback method using individual deletes with proper filters
    const { error: votesError } = await supabase.from('votes').delete().not('id', 'is', null);
    if (votesError) console.error('Error deleting votes:', votesError);
    
    const { error: submissionsError } = await supabase.from('submissions').delete().not('id', 'is', null);
    if (submissionsError) console.error('Error deleting submissions:', submissionsError);
    
    const { error: activitiesError } = await supabase.from('activities').delete().not('id', 'is', null);
    if (activitiesError) console.error('Error deleting activities:', activitiesError);
    
    const { error: membersError } = await supabase.from('league_members').delete().not('id', 'is', null);
    if (membersError) console.error('Error deleting league_members:', membersError);
    
    const { error: roundsError } = await supabase.from('rounds').delete().not('id', 'is', null);
    if (roundsError) console.error('Error deleting rounds:', roundsError);
    
    const { error: leaguesError } = await supabase.from('leagues').delete().not('id', 'is', null);
    if (leaguesError) console.error('Error deleting leagues:', leaguesError);
    
    const { error: usersError } = await supabase.from('users').delete().eq('is_admin', false);
    if (usersError) console.error('Error deleting users:', usersError);
  },

  async resetLeagues(): Promise<void> {
    if (!checkSupabaseConfig()) {
      throw new Error('Supabase is not configured.');
    }

    const { error } = await supabase.rpc('reset_leagues');
    if (error) {
      console.warn('RPC function not available, using fallback:', error);
      
      // Fallback: Delete in correct order to handle foreign key constraints
      // Only delete data that belongs to leagues/rounds, not ALL data
      
      // First, get all league IDs so we only delete related data
      const { data: allLeagues, error: leaguesFetchError } = await supabase
        .from('leagues')
        .select('id');
      
      if (leaguesFetchError) {
        throw new Error(`Failed to fetch leagues: ${leaguesFetchError.message}`);
      }
      
      const leagueIds = allLeagues?.map(l => l.id) || [];
      
      if (leagueIds.length === 0) {
        // No leagues to delete
        return;
      }
      
      // Get all round IDs that belong to these leagues
      const { data: allRounds, error: roundsFetchError } = await supabase
        .from('rounds')
        .select('id')
        .in('league_id', leagueIds);
      
      if (roundsFetchError) {
        throw new Error(`Failed to fetch rounds: ${roundsFetchError.message}`);
      }
      
      const roundIds = allRounds?.map(r => r.id) || [];
      
      // 1. Delete votes that belong to submissions in these rounds
      if (roundIds.length > 0) {
        const { error: votesError } = await supabase
          .from('votes')
          .delete()
          .in('round_id', roundIds);
        
        if (votesError) {
          console.error('Error deleting votes:', votesError);
        }
        
        // 2. Delete submissions that belong to these rounds
        const { error: submissionsError } = await supabase
          .from('submissions')
          .delete()
          .in('round_id', roundIds);
        
        if (submissionsError) {
          console.error('Error deleting submissions:', submissionsError);
        }
      }
      
      // 3. Delete rounds that belong to these leagues
      const { error: roundsError } = await supabase
        .from('rounds')
        .delete()
        .in('league_id', leagueIds);
      
      if (roundsError) {
        console.error('Error deleting rounds:', roundsError);
      }
      
      // 4. Delete league memberships
      const { error: membersError } = await supabase
        .from('league_members')
        .delete()
        .in('league_id', leagueIds);
      
      if (membersError) {
        console.error('Error deleting league members:', membersError);
      }
      
      // 5. Delete activities that reference these leagues
      const { error: activitiesError } = await supabase
        .from('activities')
        .delete()
        .in('league_id', leagueIds);
      
      if (activitiesError) {
        console.error('Error deleting activities:', activitiesError);
      }
      
      // 6. Finally, delete leagues
      const { error: leaguesError } = await supabase
        .from('leagues')
        .delete()
        .in('id', leagueIds);
      
      if (leaguesError) {
        throw new Error(`Failed to reset leagues: ${leaguesError.message}`);
      }
    }
  },

  async resetRounds(): Promise<void> {
    if (!checkSupabaseConfig()) {
      throw new Error('Supabase is not configured.');
    }

    const { error } = await supabase.rpc('reset_rounds');
    if (error) {
      console.warn('RPC function not available, using fallback:', error);
      throw new Error(`Failed to reset rounds: ${error.message}`);
    }
  },

  async resetSubmissions(): Promise<void> {
    if (!checkSupabaseConfig()) {
      throw new Error('Supabase is not configured.');
    }

    const { error } = await supabase.rpc('reset_submissions');
    if (error) {
      console.warn('RPC function not available, using fallback:', error);
      throw new Error(`Failed to reset submissions: ${error.message}`);
    }
  },

  async resetVotes(): Promise<void> {
    if (!checkSupabaseConfig()) {
      throw new Error('Supabase is not configured.');
    }

    const { error } = await supabase.rpc('reset_votes');
    if (error) {
      console.warn('RPC function not available, using fallback:', error);
      throw new Error(`Failed to reset votes: ${error.message}`);
    }
  },

  async resetActivities(): Promise<void> {
    if (!checkSupabaseConfig()) {
      throw new Error('Supabase is not configured.');
    }

    const { error } = await supabase.rpc('reset_activities');
    if (error) {
      console.warn('RPC function not available, using fallback:', error);
      throw new Error(`Failed to reset activities: ${error.message}`);
    }
  },

  async resetUsers(): Promise<void> {
    if (!checkSupabaseConfig()) {
      throw new Error('Supabase is not configured.');
    }

    const { error } = await supabase.rpc('reset_users');
    if (error) {
      console.warn('RPC function not available, using fallback:', error);
      throw new Error(`Failed to reset users: ${error.message}`);
    }
  },
};


// Playlist Links API
export const playlistLinkAPI = {
  /**
   * Get playlist links for a specific playlist
   */
  async getPlaylistLinks(
    playlistType: 'season' | 'round' | 'league' | 'curated' | 'personal',
    playlistId: string
  ): Promise<DatabasePlaylistLink[]> {
    if (!checkSupabaseConfig()) {
      throw new Error('Supabase is not configured.');
    }

    const { data, error } = await supabase
      .from('playlist_links')
      .select('*')
      .eq('playlist_type', playlistType)
      .eq('playlist_id', playlistId)
      .order('platform', { ascending: true });

    if (error) {
      throw new Error(`Failed to fetch playlist links: ${error.message}`);
    }

    return data || [];
  },

  /**
   * Create or update a playlist link
   */
  async upsertPlaylistLink(link: {
    playlist_type: 'season' | 'round' | 'league' | 'curated' | 'personal';
    playlist_id: string;
    platform: 'spotify' | 'apple_music' | 'amazon_music' | 'youtube' | 'tidal' | 'deezer' | 'soundiiz';
    url: string;
    playlist_name?: string;
    created_by?: string;
  }): Promise<DatabasePlaylistLink> {
    if (!checkSupabaseConfig()) {
      throw new Error('Supabase is not configured.');
    }

    const { data, error } = await supabase
      .from('playlist_links')
      .upsert(
        {
          playlist_type: link.playlist_type,
          playlist_id: link.playlist_id,
          platform: link.platform,
          url: link.url,
          playlist_name: link.playlist_name || null,
          created_by: link.created_by || null,
        },
        {
          onConflict: 'playlist_type,playlist_id,platform',
        }
      )
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to save playlist link: ${error.message}`);
    }

    return data;
  },

  /**
   * Delete a playlist link
   */
  async deletePlaylistLink(
    playlistType: 'season' | 'round' | 'league' | 'curated' | 'personal',
    playlistId: string,
    platform: 'spotify' | 'apple_music' | 'amazon_music' | 'youtube' | 'tidal' | 'deezer' | 'soundiiz'
  ): Promise<void> {
    if (!checkSupabaseConfig()) {
      throw new Error('Supabase is not configured.');
    }

    const { error } = await supabase
      .from('playlist_links')
      .delete()
      .eq('playlist_type', playlistType)
      .eq('playlist_id', playlistId)
      .eq('platform', platform);

    if (error) {
      throw new Error(`Failed to delete playlist link: ${error.message}`);
    }
  },

  /**
   * Get all playlist links for multiple playlists (batch)
   */
  async getBatchPlaylistLinks(
    playlistType: 'season' | 'round' | 'league' | 'curated' | 'personal',
    playlistIds: string[]
  ): Promise<DatabasePlaylistLink[]> {
    if (!checkSupabaseConfig()) {
      throw new Error('Supabase is not configured.');
    }

    if (playlistIds.length === 0) {
      return [];
    }

    const { data, error } = await supabase
      .from('playlist_links')
      .select('*')
      .eq('playlist_type', playlistType)
      .in('playlist_id', playlistIds)
      .order('platform', { ascending: true });

    if (error) {
      throw new Error(`Failed to fetch playlist links: ${error.message}`);
    }

    return data || [];
  },
};

// Curated Playlist API
export const curatedPlaylistAPI = {
  /**
   * Get all curated playlists
   */
  async getCuratedPlaylists(userLeagueIds?: string[]): Promise<CuratedPlaylist[]> {
    if (!checkSupabaseConfig()) {
      return [];
    }

    // Fetch all curated playlists with their season data
    const { data, error } = await supabase
      .from('curated_playlists')
      .select(`
        *,
        seasons:seasons!curated_playlists_season_id_fkey(
          league_id
        )
      `)
      .order('sort_index', { ascending: false, nullsLast: true })
      .order('created_at', { ascending: false });

    if (error) {
      console.error('[getCuratedPlaylists] Error:', error);
      return [];
    }

    // Filter by user's leagues if provided
    let filteredData = data || [];
    if (userLeagueIds && userLeagueIds.length > 0) {
      // First, get all seasons that belong to these leagues
      const { data: leagueSeasons, error: seasonsError } = await supabase
        .from('seasons')
        .select('id, name, league_id')
        .in('league_id', userLeagueIds);
      
      if (seasonsError) {
        console.error('[getCuratedPlaylists] Error fetching league seasons:', seasonsError);
      }
      
      console.log('[getCuratedPlaylists] Found', leagueSeasons?.length || 0, 'seasons for leagues:', userLeagueIds, 'seasons:', leagueSeasons?.map((s: any) => ({ name: s.name, league_id: s.league_id })));
      
      const leagueSeasonNames = new Set((leagueSeasons || []).map((s: any) => s.name));
      const leagueSeasonIds = new Set((leagueSeasons || []).map((s: any) => s.id));
      const leagueSeasonIdToLeague = new Map(
        (leagueSeasons || []).map((s: any) => [s.id, s.league_id]),
      );
      
      filteredData = filteredData.filter((playlist: any) => {
        // If playlist has a season_id, check if season belongs to the specified leagues
        if (playlist.season_id) {
          const season = playlist.seasons;
          const seasonLeagueId =
            season?.league_id ||
            leagueSeasonIdToLeague.get(playlist.season_id) ||
            null;
          if (!seasonLeagueId) {
            console.log('[getCuratedPlaylists] Playlist has season_id but no season league_id:', playlist.id, playlist.name, 'season:', season);
            return false;
          }
          const belongsToLeague = userLeagueIds.includes(seasonLeagueId);
          if (!belongsToLeague) {
            console.log('[getCuratedPlaylists] ❌ Excluding playlist - season belongs to different league:', playlist.name, 'season league_id:', seasonLeagueId, 'filtered league_ids:', userLeagueIds);
          } else {
            console.log('[getCuratedPlaylists] ✅ Including playlist - season belongs to league:', playlist.name, 'season league_id:', seasonLeagueId);
          }
          return belongsToLeague;
        }
        
        // If no season_id, check if playlist filters reference seasons from the specified leagues
        if (playlist.filters && playlist.filters.seasons && Array.isArray(playlist.filters.seasons)) {
          // Check if ALL of the playlist's season filters match seasons from the specified leagues
          // This ensures playlists with mixed seasons (BLIS + Masters) are excluded
          const allSeasonsMatch = playlist.filters.seasons.every((seasonName: string) => 
            leagueSeasonNames.has(seasonName)
          );
          if (!allSeasonsMatch) {
            console.log('[getCuratedPlaylists] Playlist filters reference seasons not in league:', playlist.name, 'filters.seasons:', playlist.filters.seasons, 'league seasons:', Array.from(leagueSeasonNames));
          }
          return allSeasonsMatch;
        }
        
        // Universal playlists (no season_id and no season filters) - include for all leagues
        console.log('[getCuratedPlaylists] ✅ Including universal playlist:', playlist.name);
        return true;
      });
      
      console.log('[getCuratedPlaylists] Filtered playlists:', filteredData.length, 'from', data.length, 'total');
    }

    return filteredData.map(dbCuratedPlaylistToCuratedPlaylist);
  },

  /**
   * Create a curated playlist
   */
  async createCuratedPlaylist(playlist: {
    name: string;
    description?: string;
    playlist_type?: 'curated' | 'system' | 'season_top3';
    filters: Record<string, any>;
    created_by?: string;
    is_system_generated?: boolean;
    season_id?: string;
  }): Promise<CuratedPlaylist> {
    if (!checkSupabaseConfig()) {
      throw new Error('Supabase is not configured.');
    }

    const { data, error } = await supabase
      .from('curated_playlists')
      .insert({
        name: playlist.name,
        description: playlist.description || null,
        playlist_type: playlist.playlist_type || 'curated',
        filters: playlist.filters,
        created_by: playlist.created_by || null,
        is_system_generated: playlist.is_system_generated || false,
        season_id: playlist.season_id || null,
      })
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to create curated playlist: ${error.message}`);
    }

    return dbCuratedPlaylistToCuratedPlaylist(data);
  },

  /**
   * Update a curated playlist
   */
  async updateCuratedPlaylist(id: string, updates: Partial<{
    name: string;
    description?: string;
    filters: Record<string, any>;
    smartlink_url?: string | null;
  }>): Promise<CuratedPlaylist> {
    if (!checkSupabaseConfig()) {
      throw new Error('Supabase is not configured.');
    }

    const { data, error } = await supabase
      .from('curated_playlists')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', id)
      .select()
      .single();

    if (error) {
      throw new Error(`Failed to update curated playlist: ${error.message}`);
    }

    return dbCuratedPlaylistToCuratedPlaylist(data);
  },

  /**
   * Delete a curated playlist
   */
  async deleteCuratedPlaylist(id: string): Promise<void> {
    if (!checkSupabaseConfig()) {
      throw new Error('Supabase is not configured.');
    }

    const { error } = await supabase
      .from('curated_playlists')
      .delete()
      .eq('id', id);

    if (error) {
      throw new Error(`Failed to delete curated playlist: ${error.message}`);
    }
  },

  /**
   * Create or update "Season - Top 3" playlist for a season
   */
  async createSeasonTop3Playlist(seasonId: string, seasonName: string): Promise<CuratedPlaylist> {
    if (!checkSupabaseConfig()) {
      throw new Error('Supabase is not configured.');
    }

    // Get all rounds for this season
    const { data: rounds, error: roundsError } = await supabase
      .from('rounds')
      .select('id')
      .eq('season_id', seasonId);

    if (roundsError) {
      throw new Error(`Failed to fetch rounds: ${roundsError.message}`);
    }

    if (!rounds || rounds.length === 0) {
      throw new Error('No rounds found for this season');
    }

    const roundIds = rounds.map(r => r.id);

    // Get top 3 submissions for each round
    const top3Submissions: string[] = [];
    for (const roundId of roundIds) {
      const { data: submissions, error: submissionsError } = await supabase
        .from('submissions')
        .select('id, total_points')
        .eq('round_id', roundId)
        .order('total_points', { ascending: false })
        .limit(3);

      if (!submissionsError && submissions) {
        top3Submissions.push(...submissions.map(s => s.id));
      }
    }

    const playlistName = `${seasonName} - Top 3`;
    const filters = {
      seasons: [seasonName],
      ranks: [1, 2, 3],
      topThreeOnly: true,
    };

    // Check if playlist already exists
    const { data: existing } = await supabase
      .from('curated_playlists')
      .select('*')
      .eq('name', playlistName)
      .eq('playlist_type', 'season_top3')
      .eq('season_id', seasonId)
      .maybeSingle();

    if (existing) {
      // Update existing
      return this.updateCuratedPlaylist(existing.id, {
        filters,
      });
    } else {
      // Create new
      return this.createCuratedPlaylist({
        name: playlistName,
        description: `Top 3 songs from each round in ${seasonName}`,
        playlist_type: 'season_top3',
        filters,
        is_system_generated: true,
        season_id: seasonId,
      });
    }
  },
};

// Helper function to convert database curated playlist to app format
function dbCuratedPlaylistToCuratedPlaylist(db: DatabaseCuratedPlaylist): CuratedPlaylist {
  return {
    id: db.id,
    name: db.name,
    description: db.description || undefined,
    filters: {
      seasons: db.filters.seasons || undefined,
      submitters: db.filters.submitters || undefined,
      ranks: db.filters.ranks || undefined,
      topThreeOnly: db.filters.topThreeOnly || false,
      roundIds: db.filters.roundIds || db.filters.round_ids || undefined,
    },
    created_by: db.created_by || '',
    sort_index: db.sort_index ?? undefined,
    smartlink_url: db.smartlink_url || undefined,
    created_at: db.created_at,
    updated_at: db.updated_at,
  };
}

